import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

/** The finished .icb (base64) plus what the engine reported about it. */
export type BuildWorkflowResult = {
  filename: string;
  customer: string;
  paperSource: string;
  warnings: string[];
  icbBase64: string;
};

/** The form boxes as the engine's drafting step reads them, keyed like the page's form. */
export type WorkflowForm = Record<string, string>;

/** Form boxes mapped from a brief, plus which ones the SE should check. */
export type FillFormResult = {
  form: WorkflowForm;
  /** Boxes the brief didn't cover, left blank. */
  notFound: string[];
  /** Boxes filled by inference rather than from the brief's words. */
  guessed: string[];
  /** Amounts, percentages and periods in the brief that no box carries. */
  droppedFigures: string[];
  /** Values the form can't take, e.g. an unknown agreement type. */
  errors: string[];
};

/** One checker finding on a drafted config. */
export type DraftIssue = { code: string; path: string; message: string };

export type DraftResult = {
  config: Record<string, unknown>;
  assumptions: string[];
  /** Build is blocked until these are fixed. */
  errors: DraftIssue[];
  notes: DraftIssue[];
};

export type BuildJobStatus = 'running' | 'needs_fixes' | 'done' | 'failed';

export type BuildJobError = {
  code: string;
  message: string;
  retryable: boolean;
  warnings: string[];
};

export type BuildJobSummary = {
  id: string;
  customer?: string;
  status: BuildJobStatus;
  stage?: 'drafting' | 'building';
  error?: BuildJobError;
  createdAt: string;
  updatedAt: string;
};

/** A draft-then-build job; a failed one keeps its draft when drafting finished. */
export type BuildJob = BuildJobSummary & {
  draft?: DraftResult;
  build?: BuildWorkflowResult;
};

export type StartBuildJobRequest = {
  form: WorkflowForm;
  template?: File;
};

export class ArmoryBuildError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** The backend's error code, e.g. build_failed or engine_timeout. */
    readonly code: string,
    readonly retryable: boolean,
    readonly warnings: string[] = [],
  ) {
    super(message);
    this.name = 'ArmoryBuildError';
  }
}

/**
 * Errors that say nothing about the job itself: the connection, the load balancer, or an
 * expired sign-in (Backstage refreshes it on the next request). Polling retries these.
 */
export const isTransientError = (error: unknown) =>
  error instanceof ArmoryBuildError &&
  (error.code === 'network' || error.code === 'gateway' || error.status === 401);

export interface ArmoryApi {
  fillForm(brief: string): Promise<FillFormResult>;
  /** Starts drafting and building from the form in the background; returns the job id. */
  startBuildJob(request: StartBuildJobRequest): Promise<string>;
  getBuildJob(id: string): Promise<BuildJob>;
  /** The caller's recent jobs, newest first. */
  listBuildJobs(): Promise<BuildJobSummary[]>;
}

export const armoryApiRef = createApiRef<ArmoryApi>({
  id: 'plugin.armory.service',
});

export class ArmoryClient implements ArmoryApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async fillForm(brief: string): Promise<FillFormResult> {
    return this.request<FillFormResult>('/fill', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ brief }),
    });
  }

  async startBuildJob({ form, template }: StartBuildJobRequest): Promise<string> {
    const body = new FormData();
    body.append('form', JSON.stringify(form));
    if (template) body.append('template', template, template.name);
    const { id } = await this.request<{ id: string }>('/jobs', {
      method: 'POST',
      body,
    });
    return id;
  }

  async getBuildJob(id: string): Promise<BuildJob> {
    return this.request<BuildJob>(`/jobs/${encodeURIComponent(id)}`);
  }

  async listBuildJobs(): Promise<BuildJobSummary[]> {
    const { jobs } = await this.request<{ jobs: BuildJobSummary[] }>('/jobs');
    return jobs;
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const url = `${await this.discoveryApi.getBaseUrl('armory')}${path}`;
    let res: Response;
    try {
      res = await this.fetchApi.fetch(url, init);
    } catch {
      throw new ArmoryBuildError(
        "Couldn't reach Backstage. Check your connection and try again.",
        0,
        'network',
        true,
      );
    }
    if (!res.ok) throw await toBuildError(res);
    try {
      return (await res.json()) as T;
    } catch {
      throw new ArmoryBuildError(
        'The request finished but the response was unreadable. Try again.',
        res.status,
        'bad_response',
        true,
      );
    }
  }
}

// Gateway errors from the load balancer or proxy arrive without our JSON envelope.
const GATEWAY_STATUSES = new Set([502, 503, 504]);

async function toBuildError(res: Response): Promise<ArmoryBuildError> {
  const body = await res.json().catch(() => undefined);
  // Our backend's errors carry code/retryable/warnings; Backstage's own (e.g. 401) only name/message.
  const error = body?.error ?? {};
  if (typeof error.code !== 'string' && GATEWAY_STATUSES.has(res.status)) {
    return new ArmoryBuildError(
      res.status === 504
        ? 'The request timed out. Try again.'
        : 'Backstage is temporarily unavailable. Try again.',
      res.status,
      'gateway',
      true,
    );
  }
  const warnings = Array.isArray(error.warnings)
    ? error.warnings.filter((w: unknown): w is string => typeof w === 'string')
    : [];
  return new ArmoryBuildError(
    typeof error.message === 'string' && error.message
      ? error.message
      : `The request failed (HTTP ${res.status}).`,
    res.status,
    typeof error.code === 'string' ? error.code : 'unknown',
    error.retryable === true,
    warnings,
  );
}
